/* eslint-disable react/react-in-jsx-scope */
import { useTranslation } from "react-i18next";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";

import { langs } from "@/constants/constants";
import { AlertTriangle, Check, ChevronDown, Clock3, Mail } from "lucide-react";
import { useEffect, useState } from "react";
import "react-loading-skeleton/dist/skeleton.css";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { LoadingSpinner } from "@/components/ui/loadingSpinner";
import { toast } from "@/components/ui/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff, Save } from "lucide-react";
import { useForm } from "react-hook-form";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import PasswordStrengthCheck from "../../dashboard/components/subcontractor/PasswordStrengthCheck";
import type { CSSProperties } from "react";

import { useHidePostActions } from "@/auth/hooks/useHidePostActions";
import { cn } from "@/libs/utils";
import "react-loading-skeleton/dist/skeleton.css";
import slugify from "slugify";

const HTML_TAG_REGEX = /<\/?[a-z][\s\S]*>/i;
const hasHtmlContent = (value: string): boolean => HTML_TAG_REGEX.test(value);

const normalizeDateOnly = (raw?: string | null): string | null => {
  if (!raw || !raw.trim()) return null;
  const direct = raw.trim().slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(direct)) return direct;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDateForDisplay = (raw?: string | null): string | null => {
  const normalized = normalizeDateOnly(raw);
  if (!normalized) return null;
  const [year, month, day] = normalized.split("-");
  if (!year || !month || !day) return normalized;
  return `${day}/${month}/${year}`;
};

type SubscriptionBranding = {
  logoUrl?: string | null;
  theme?: {
    primary?: string;
    primaryForeground?: string;
    background?: string;
    foreground?: string;
    border?: string;
    muted?: string;
    mutedForeground?: string;
  } | null;
};

const SubscriptionForMeta = () => {
  const { t } = useTranslation("general");
  const { t: tHome } = useTranslation("home");
  const hidePostActions = useHidePostActions();
  const { i18n } = useTranslation();
  const [counter, setCounter] = useState(10);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const { metacode, callId } = useParams<{
    metacode: string;
    callId: string;
  }>();
  const [searchParams] = useSearchParams();
  const restriction = searchParams.get("restriction");
  const [hasRestriction, setHasRestriction] = useState(
    restriction ? "&restriction=" + restriction : "",
  );
  const [isCallForProject, setIsCallForProject] = useState(
    callId ? true : false,
  );
  const [idCallForProject, setIdCallForProject] = useState(
    callId ? "&idCallForProject=" + callId.split("-")[1] : "",
  );
  const [nomProgramme, setNomProgramme] = useState("");
  const [programIntroduction, setProgramIntroduction] = useState("");
  const [programDeadline, setProgramDeadline] = useState<string | null>(null);
  const [showAccountForm, setShowAccountForm] = useState(!callId);
  const [newLinkGenerated, setNewLinkGenerated] = useState("");
  const [branding, setBranding] = useState<SubscriptionBranding | null>(null);

  const navigate = useNavigate();
  const [loadingCheck, setLoadingCheck] = useState(true);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${import.meta.env.VITE_API_ENDPOINT}/user-check?codeMeta=${metacode}${idCallForProject}${hasRestriction}`,
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const json = await response.json();

        if (json.success) {
          setFirstName(json.firstname);
          setLastName(json.lastname);
          setNomProgramme(json.programName);
          setBranding(json.branding ?? null);
          setProgramDeadline(typeof json.dateFin === "string" ? json.dateFin : null);
          setProgramIntroduction(
            String(
              json.introduction ??
              json.programIntroduction ??
              json.callIntroduction ??
              "",
            ),
          );
          setLoadingCheck(true);
        } else {
          // navigate('/error');
        }
      } catch (err: any) {
        navigate("/error");
        console.log(err.message || "Unknown error");
      } finally {
        setLoadingCheck(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (isCallForProject !== true) {
      const timer = setInterval(() => {
        if (isRedirecting === true) {
          setCounter((prevCounter) => prevCounter - 1);
        }
      }, 1000);

      // Quand le compteur atteint 0, redirection
      if (counter === 0 && isRedirecting === true) {
        // localStorage.setItem("code", "Regenerate2024!");
        window.location.href = "/login";
        // window.location.href = "/login-subcontractor/authentification";
      }
      // Nettoyer l'intervalle à chaque rendu ou démontage
      return () => clearInterval(timer);
    }
  }, [counter, isRedirecting]);

  const [loadingSave, setLoadingSave] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
  const [isPasswordOk, setIsPasswordOk] = useState(true);
  const [newLink, setNewLink] = useState<string | null>(null);
  const [emailAlreadyUsed, setEmailAlreadyUsed] = useState(false);
  const [linkedExistingAccount, setLinkedExistingAccount] = useState(false);
  const resolvedLogo = branding?.logoUrl?.trim() ? branding.logoUrl : "/logo.svg";
  const theme = branding?.theme ?? null;
  const pageStyle = {
    "--primary": theme?.primary || "85 49% 58%",
    "--primary-foreground": theme?.primaryForeground || "210 40% 98%",
    "--border": theme?.border || "214.3 31.8% 91.4%",
    "--muted": theme?.muted || "0 0% 96.21%",
    "--muted-foreground": theme?.mutedForeground || "215.4 16.3% 46.9%",
  } as CSSProperties;

  function getFormSchema(isEditMode: boolean, isPasswordOk: boolean) {
    const baseFields = {
      lastname: z.string().min(2, {
        message: t("Le nom de famille doit contenir au moins 2 caractères."),
      }),
      firstname: z.string().min(2, {
        message: t("Le prénom doit contenir au moins 2 caractères."),
      }),
      email: z.string().email({
        message: t("L'adresse e-mail n'est pas valide."),
      }),
    };

    const passwordField = z
      .string()
      .min(8, t("Le mot de passe doit comporter au moins 8 caractères."))
      .refine((val) => /[a-z]/.test(val), {
        message: t(
          "Le mot de passe doit contenir au moins une lettre minuscule.",
        ),
      })
      .refine((val) => /[A-Z]/.test(val), {
        message: t(
          "Le mot de passe doit contenir au moins une lettre majuscule.",
        ),
      })
      .refine((val) => /\d/.test(val), {
        message: t("Le mot de passe doit contenir au moins un chiffre."),
      })
      .refine((val) => /[!@#$%^&*(),.?":{}|<>]/.test(val), {
        message: t(
          "Le mot de passe doit contenir au moins un caractère spécial.",
        ),
      });

    const confirmPasswordField = z
      .string()
      .min(8, {
        message: t(
          "La confirmation du mot de passe doit contenir au moins 8 caractères.",
        ),
      })
      .regex(passwordRegex, {
        message: t(
          "La confirmation du mot de passe doit contenir au moins une majuscule, une minuscule et un chiffre.",
        ),
      });

    const shouldValidatePassword = !isEditMode || (isEditMode && !isPasswordOk);

    const schema = z.object({
      ...baseFields,
      ...(shouldValidatePassword
        ? {
          password: passwordField,
          confirmPassword: confirmPasswordField,
        }
        : {
          password: z.string().optional(),
          confirmPassword: z.string().optional(),
        }),
    });

    // Ajoute la validation que les deux mdp doivent correspondre
    return shouldValidatePassword
      ? schema.refine((data) => data.password === data.confirmPassword, {
        path: ["confirmPassword"],
        message: t("Les mots de passe ne correspondent pas."),
      })
      : schema;
  }

  const formSchema = getFormSchema(isPasswordOk);
  type SubscriptionFormValues = z.infer<typeof formSchema>;
  const form = useForm<SubscriptionFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstname: "",
      lastname: "",
      email: "",
      password: isCallForProject ? "DefaultPassword2025!" : "",
      confirmPassword: isCallForProject ? "DefaultPassword2025!" : "",
    },
  });
  const [pendingExistingAccount, setPendingExistingAccount] = useState<{
    values: SubscriptionFormValues;
    message: string;
  } | null>(null);

  const password = form.watch("password");
  const hasProgramIntroduction = programIntroduction.trim().length > 0;
  const displayedProgramDeadline = formatDateForDisplay(programDeadline);
  const isCallExpired = (() => {
    const deadline = normalizeDateOnly(programDeadline);
    if (!deadline) return false;
    const now = new Date();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
      now.getDate(),
    ).padStart(2, "0")}`;
    return today > deadline;
  })();

  const submitSubscription = async (
    data: SubscriptionFormValues,
    confirmExistingUser = false,
  ) => {
    setLoadingSave(true);

    const urlApi = `${import.meta.env.VITE_IP_API}/api/subcontractor/subscription/${metacode}`;

    let res: Response;
    let result: any = null;
    try {
      res = await fetch(urlApi, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          firstname: data.firstname,
          lastname: data.lastname,
          addresseEmail: data.email,
          mdp: data.password,
          isCallForProject: isCallForProject,
          idCall: callId ? parseInt(callId.split("-")[1]) : null,
          restriction,
          confirmExistingUser,
        }),
      });
      result = await res.json().catch(() => null);
    } catch {
      toast({
        title: "Une erreur est survenue",
        duration: 3000,
      });
      setLoadingSave(false);
      setIsRedirecting(false);
      return;
    }

    if (res.status === 409 && result?.requiresConfirmation) {
      setPendingExistingAccount({
        values: data,
        message:
          result.message ||
          "Cette adresse email existe déjà. Voulez-vous rattacher ce compte comme intervenant ?",
      });
      setLoadingSave(false);
      return;
    }

    const responseData = res.ok ? result : null;

    if (responseData === null) {
      console.log("Inexistant email");
      toast({
        title: t(result?.message || "L'adresse email n'existe pas"),
        duration: 3000,
      });
      setLoadingSave(false);
      setIsRedirecting(false);
    } else if (responseData.success !== undefined && responseData.success) {
      setIsRedirecting(true);
      setNewLinkGenerated(responseData.newLink);
      setPendingExistingAccount(null);
      setLinkedExistingAccount(confirmExistingUser);
    } else if (responseData.error && responseData.lookupBy) {
      setLoadingSave(false);
      setIsRedirecting(false);
      setCounter(10);
      const hasCallLoginLink =
        (responseData.lookupBy == "intervenant_id" || responseData.lookupBy == "intervenant_email") &&
        responseData.linkedCallProjects?.link;

      if (hasCallLoginLink) {
        setNewLink(window.location.origin +
          "/authentification-cfp/" + responseData.linkedCallProjects.link + "/" + slugify(responseData.userTemp.lastname + responseData.userTemp.firstname, {
            lower: true,
            strict: true,
            trim: true,
          }));
      } else {
        setNewLink("/login");
      }
      setEmailAlreadyUsed(true);


      if (responseData.message) {
        toast({
          title: t(responseData.message),
          duration: 3000,
        });
      } else {
        toast({
          title: t("L'adresse email est déjà utilisé par un autre utilisateur"),
          duration: 3000,
        });
      }
    } else if (responseData && responseData.message && !responseData.lookupBy) {
      setLoadingSave(false);
      setIsRedirecting(false);
      setCounter(10);

      toast({
        title: t(responseData.message),
        duration: 3000,
      });
    } else {
      // If not successful, check for a specific message from the API
      if (responseData?.message) {
        toast({
          title: t("L'adresse email n'existe pas"), // Use the message directly from the API response
          duration: 3000,
        });
      } else {
        // Fallback for generic errors
        toast({
          title: "Une erreur est survenue",
          duration: 3000,
        });
      }
      setLoadingSave(false);
      setIsRedirecting(false);

      if (!isCallForProject) {
        setCounter(10);
      }
    }
  };

  const handleSave = async (data: SubscriptionFormValues) => {
    await submitSubscription(data);
  };

  const handleConfirmExistingAccount = async () => {
    if (!pendingExistingAccount) {
      return;
    }

    await submitSubscription(pendingExistingAccount.values, true);
  };
  useEffect(() => {
    // Vérifie si toutes les règles sont respectées
    const isValid =
      password?.length >= 8 &&
      /[a-z]/.test(password) &&
      /[A-Z]/.test(password) &&
      /\d/.test(password) &&
      /[!@#$%^&*(),.?":{}|<>]/.test(password);

    setIsPasswordValid(isValid);
  }, [password]);

  useEffect(() => {
    console.log("newLink", newLink);
  }, [newLink])
  return (
    <div className="container mb-8" style={pageStyle}>
      <nav className="relative flex h-16 items-center border-b md:h-24">
        <ul className="flex w-full items-center">
          <li>
            <div className="flex h-14 w-28 items-center justify-center md:h-20 md:w-40">
              {!loadingCheck && (
                <Link
                  to="https://verdeo.fr"
                  className="flex h-full w-full items-center justify-center"
                >
                  <img
                    src={resolvedLogo}
                    alt="logo"
                    className="max-h-full max-w-full object-contain"
                  />
                </Link>
              )}
            </div>
          </li>
          <li className="ml-auto mr-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild className="cursor-pointer">
                <div className="flex items-center">
                  {i18n === undefined ||
                    i18n === null ||
                    i18n.language === undefined ||
                    i18n.language === null
                    ? "FR"
                    : i18n.language.split("-")[0].toUpperCase()}

                  <ChevronDown className="size-4 opacity-50" />
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="min-w-[10px]">
                <DropdownMenuGroup className="w-[50px]">
                  {langs.map((lang, key) => (
                    <DropdownMenuItem
                      className="cursor-pointer hover:bg-white focus:bg-white focus:text-black"
                      onClick={() => {
                        i18n.language !== lang && i18n.changeLanguage(lang);
                      }}
                      key={key}
                    >
                      {lang.toUpperCase()}
                      {/* <span className={"fi fi-"+(lang === "en" ? "gb" : lang)+(i18n.language === lang ? " opacity-70 cursor-not-allowed" : "")} style={{
                                        width: '45px',
                                        height: '25px',
                                        backgroundSize: 'contain',
                                    }}></span> */}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </li>
          <li className="mr-4 text-right">
            <a
              href="https://verdeo.fr/contact/"
              className="group relative flex size-[42px] items-center justify-center rounded-full border border-foreground md:size-max md:border-0"
            >
              <span className="hidden text-xs md:block md:text-base">
                {t("Contactez-nous")}!{" "}
              </span>
              <span className="absolute left-0 top-full hidden h-px w-0 bg-primary transition-all group-hover:w-full md:block"></span>
              <Mail className="block stroke-1 md:hidden" />
            </a>
          </li>
        </ul>
      </nav>
      {loadingCheck == true ? (
        <UserCheckSkeleton />
      ) : (
        <>
          {isRedirecting === false && (
            <>
              {isCallForProject && !showAccountForm && (
                <div className="mx-auto mb-5 mt-12 w-full max-w-5xl px-2 md:px-0">
                  <div className="w-full rounded-2xl border bg-white p-6 shadow-sm md:p-8">
                    {isCallExpired && (
                      <div className="mb-6 rounded-2xl border border-red-300 bg-red-50 p-5">
                        <div className="flex items-start gap-3">
                          <div className="rounded-full bg-red-100 p-2">
                            <AlertTriangle className="size-5 text-red-600" />
                          </div>
                          <div className="space-y-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <h2 className="text-lg font-semibold text-red-900">
                                Les candidatures sont closes
                              </h2>
                              {displayedProgramDeadline && (
                                <span className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-white px-2.5 py-1 text-xs font-medium text-red-700">
                                  <Clock3 className="size-3.5" />
                                  Date limite : {displayedProgramDeadline}
                                </span>
                              )}
                            </div>
                            <p className="text-sm leading-6 text-red-800">
                              La période de dépôt des candidatures pour cet appel à projets est terminée.
                              Il n’est désormais plus possible de candidater via ce lien.
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    {hasProgramIntroduction ? (
                      hasHtmlContent(programIntroduction) ? (
                        <div
                          className="prose prose-sm max-w-none text-gray-700 md:prose-base"
                          dangerouslySetInnerHTML={{ __html: programIntroduction }}
                        />
                      ) : (
                        <p className="text-sm text-gray-700 whitespace-pre-line md:text-base">
                          {programIntroduction}
                        </p>
                      )
                    ) : (
                      <div className="flex flex-wrap lg:items-center">
                        <div className="order-2 mt-6 basis-full md:mt-0 lg:order-1 lg:basis-2/5 lg:pr-8">
                          <img
                            src="/verdeo-home.jpg"
                            alt="verdeo home"
                            className="w-full rounded-xl object-cover"
                          />
                        </div>
                        <div className="order-1 basis-full lg:order-2 lg:basis-3/5 lg:pl-4">
                          <h2 className="mb-4 text-xl font-bold md:text-2xl">
                            {!isCallForProject
                              ? t("Le porteur de projet {{pp}} vous a référencé sur Verdeo", {
                                pp: firstName + " " + lastName,
                              })
                              : <>
                                {t("Le porteur de projet {{pp}} vous invite à répondre à l'appel à projets", {
                                  pp: firstName + " " + lastName,
                                })}{" "}
                                <span className="text-primary">{nomProgramme}</span>
                              </>
                            }
                          </h2>
                          <p className="mb-3 text-sm text-gray-700 md:text-base">
                            {t("Un porteur de projet vous a transmis ce lien afin de vous permettre de soumettre votre candidature dans le cadre de son appel à projets sur Verdeo.")}
                          </p>
                          <p className="mb-3 text-sm text-gray-700 md:text-base">
                            {t("Pour accéder à cet appel à projets et déposer votre dossier, nous vous invitons à créer votre compte sur la plateforme. Vous pourrez ainsi présenter votre projet directement au porteur concerné.")}
                          </p>
                          <p className="text-sm text-gray-700 md:text-base">
                            {t("Verdeo met à votre disposition des outils de suivi et de traçabilité pour accompagner votre projet, du dépôt de candidature jusqu'à sa mise en œuvre.")}
                          </p>
                        </div>
                      </div>
                    )}

                    {!hidePostActions && !isCallExpired && (
                      <div className="mt-6 w-full flex justify-end">
                          <Button
                            type="button"
                            className="gap-2 bg-primary text-primary-foreground hover:opacity-90 md:mt-8"
                            onClick={() => setShowAccountForm(true)}
                          >
                          {t("Commencer")}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {(!isCallForProject || showAccountForm) && !(isCallForProject && isCallExpired) && (
                <div
                  className={cn(
                    "mx-auto mb-5 mt-8 flex flex-wrap lg:w-2/4",
                    pendingExistingAccount && "pointer-events-none opacity-40"
                  )}
                >
                  <Card>
                    <CardHeader>
                      <CardTitle>{t("créez votre compte")}</CardTitle>
                      <CardDescription>
                        {/* {!isCallForProject
                          ? t("Le porteur de projet vous a référencé", {
                            pp: firstName + " " + lastName,
                          })
                          : t(
                            "Le porteur de projet vous invite à répondre à l’appel à projet",
                            {
                              pp: firstName + " " + lastName,
                              programme: nomProgramme,
                            },
                          )} */}
                        <br />

                        {emailAlreadyUsed ? (
                          <div className="mt-2 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-left">
                            <div className="flex items-start gap-3">
                              <div className="mt-0.5 rounded-full bg-amber-100 p-2">
                                <AlertTriangle className="size-4 text-amber-700" />
                              </div>
                              <div className="flex-1 space-y-3">
                                <div className="space-y-1">
                                  <p className="text-sm font-semibold text-amber-900">
                                    Adresse e-mail déjà enregistrée
                                  </p>
                                  <p className="text-sm leading-6 text-amber-900/90">
                                    L&apos;adresse e-mail{" "}
                                    <span className="font-semibold">
                                      {form.getValues("email")}
                                    </span>{" "}
                                    est déjà associée à un compte candidat.
                                  </p>
                                  <p className="text-sm leading-6 text-amber-900/90">
                                    Connectez-vous directement via le lien d&apos;accès ci-dessous pour poursuivre votre candidature.
                                  </p>
                                </div>
                                <div className="rounded-xl border border-amber-200 bg-white px-3 py-2 text-xs text-slate-600 break-all">
                                  {newLink ?? "/login"}
                                </div>
                                <div className="flex flex-wrap gap-2">
                                  <a
                                    href={newLink ?? "/login"}
                                    className="inline-flex items-center justify-center rounded-xl bg-amber-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-amber-700"
                                  >
                                    Accéder à mon espace candidat
                                  </a>
                                </div>
                              </div>
                            </div>
                            <div className="hidden">
                            {t("Cette adresse e-mail est déjà enregistrée. Veuillez vous connecter via le lien suivant :")}
                            <br /> <br /><a href={newLink ?? "/login"} className="text-blue-600 underline hover:text-blue-800">{newLink}</a>
                          </div></div>
                        ) : t(
                          "Merci de vous inscrire afin d’accéder aux fonctionnalités de suivi qu’il vous offre.",
                        )}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <Form {...form}>
                        <form
                          onSubmit={form.handleSubmit(handleSave)}
                          className="space-y-8"
                        >
                          <div className="flex flex-wrap gap-4">
                            <div className="flex flex-wrap gap-4">
                              {/* Email en pleine largeur */}
                              <div className="w-full">
                                <FormField
                                  control={form.control}
                                  name="email"
                                  render={({ field }) => (
                                    <FormItem>
                                      <FormLabel>{t("Email")} *</FormLabel>
                                      <FormControl>
                                        <Input
                                          placeholder={t("Email")}
                                          {...field}
                                          type="email"
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              </div>
                              {/* Nom + Prénom */}
                              <div className="flex w-full gap-4">
                                <div className="w-1/2">
                                  <FormField
                                    control={form.control}
                                    name="lastname"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>{t("Nom")} *</FormLabel>
                                        <FormControl>
                                          <Input
                                            placeholder={t("Nom")}
                                            {...field}
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                </div>
                                <div className="w-1/2">
                                  <FormField
                                    control={form.control}
                                    name="firstname"
                                    render={({ field }) => (
                                      <FormItem>
                                        <FormLabel>{t("Prénom")} *</FormLabel>
                                        <FormControl>
                                          <Input
                                            placeholder={t("Prénom")}
                                            {...field}
                                          />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                </div>
                              </div>

                              {!isCallForProject && (
                                <div className="flex w-full gap-4">
                                  <div className="w-1/2">
                                    <FormField
                                      control={form.control}
                                      name="password"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>
                                            {t("Mot de passe")} *
                                          </FormLabel>
                                          <FormControl>
                                            <div className="relative">
                                              <Input
                                                placeholder={t("Mot de passe")}
                                                {...field}
                                                type={
                                                  showPassword ? "text" : "password"
                                                }
                                              />
                                              <button
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  setShowPassword(!showPassword);
                                                }}
                                                className="absolute right-2 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center"
                                              >
                                                {!showPassword ? (
                                                  <EyeOff />
                                                ) : (
                                                  <Eye />
                                                )}
                                              </button>
                                            </div>
                                          </FormControl>
                                          <FormMessage />
                                          {password && (
                                            <PasswordStrengthCheck
                                              password={password}
                                              isPasswordValid={isPasswordValid}
                                              className="mt-0"
                                            />
                                          )}
                                        </FormItem>
                                      )}
                                    />
                                  </div>
                                  <div className="w-1/2">
                                    <FormField
                                      control={form.control}
                                      name="confirmPassword"
                                      render={({ field }) => (
                                        <FormItem>
                                          <FormLabel>
                                            {t("Confirmation mot de passe")} *
                                          </FormLabel>
                                          <FormControl>
                                            <div className="relative">
                                              <Input
                                                placeholder={t(
                                                  "Confirmation mot de passe",
                                                )}
                                                {...field}
                                                type={
                                                  showPasswordConfirm
                                                    ? "text"
                                                    : "password"
                                                }
                                              />
                                              <button
                                                onClick={(e) => {
                                                  e.preventDefault();
                                                  setShowPasswordConfirm(
                                                    !showPasswordConfirm,
                                                  );
                                                }}
                                                className="absolute right-2 top-1/2 flex size-5 -translate-y-1/2 items-center justify-center"
                                              >
                                                {!showPasswordConfirm ? (
                                                  <EyeOff />
                                                ) : (
                                                  <Eye />
                                                )}
                                              </button>
                                            </div>
                                          </FormControl>
                                          <FormMessage />
                                        </FormItem>
                                      )}
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </form>
                      </Form>
                      <br />
                      {!hidePostActions &&
                        (loadingSave ? (
                          <div className={"w-full items-center"}>
                            <LoadingSpinner textChargement="Enregistrement en cours.." />
                          </div>
                        ) : (
                          <Button
                            onClick={form.handleSubmit(handleSave)}
                            type="button"
                            disabled={loadingSave}
                            className={
                              "mt-4 flex w-full items-center gap-2 bg-primary text-primary-foreground hover:opacity-90"
                            }
                          >
                            <Save size={18} />
                            {t("S'inscrire")}
                          </Button>
                        ))}
                    </CardContent>
                  </Card>
                </div>
              )}
            </>
          )}

          {isRedirecting === true && !isCallForProject && (
            <div className="mx-auto mb-5 mt-8 flex flex-wrap lg:w-3/4">
              <Card>
                <CardHeader>
                  <CardTitle>
                    <div className="w-full flex gap-2">
                      {linkedExistingAccount ? t("Intervenant ajouté") : t("Inscription réussie")}
                      <Check size={42} color="green" />
                    </div>
                  </CardTitle>
                  <CardDescription>
                    {linkedExistingAccount
                      ? t("Le compte existant a été rattaché comme intervenant")
                      : t("Votre inscription a été réalisée")}
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  <p className="items-center">
                    {t("Redirection dans")} <b>{counter}</b> {t("seconde")}
                    {counter > 1 ? "s" : ""}...
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {isRedirecting === true && isCallForProject && (
            <div className="mx-auto mb-5 mt-8 flex flex-wrap lg:w-3/4">
              <Card>
                <CardHeader>
                  <CardTitle>
                    <div className="w-full flex gap-2">
                      {linkedExistingAccount ? t("Intervenant ajouté") : t("Inscription réussie")}
                      <Check size={42} color="green" />
                    </div>
                  </CardTitle>
                  <CardDescription>
                    {linkedExistingAccount
                      ? t("Le compte existant a été rattaché comme intervenant de l'appel à projet")
                      : t("Votre inscription a été réalisée avec succès")}
                  </CardDescription>
                </CardHeader>

                <CardContent>
                  <p className="items-center">
                    {t("Un email avec votre code a été envoyé à l'adresse")}{" "}
                    <b>{form.getValues("email")}</b>
                  </p>
                  <br />
                  <p className="items-center">
                    {t("Veuillez vous connecter sur le lien suivant :")}
                    <br />
                    <br />
                    <a
                      href={newLinkGenerated}
                      className="text-blue-600 underline hover:text-blue-800"
                    >
                      {newLinkGenerated}
                    </a>
                  </p>
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}
      <Dialog
        open={!!pendingExistingAccount}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && !loadingSave) {
            setPendingExistingAccount(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Compte existant</DialogTitle>
            <DialogDescription>
              {pendingExistingAccount?.message}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={loadingSave}
              onClick={() => setPendingExistingAccount(null)}
            >
              Annuler
            </Button>
            <Button
              type="button"
              disabled={loadingSave}
              onClick={handleConfirmExistingAccount}
            >
              {loadingSave ? t("Confirmation...") : t("Confirmer")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const UserCheckSkeleton = () => {
  return (
    <div className="p-4 border rounded-lg shadow-sm bg-white max-w-lg mx-auto mt-4">
      {/* Avatar skeleton */}
      <div className="flex justify-center mb-4">
        <Skeleton circle width={80} height={80} />
      </div>

      {/* Nom */}
      <div className="mb-2">
        <Skeleton height={20} width={`60%`} style={{ margin: "0 auto" }} />
      </div>

      {/* Email */}
      <div className="mb-2">
        <Skeleton height={16} width={`80%`} style={{ margin: "0 auto" }} />
      </div>

      {/* Autres infos */}
      <div className="mt-4 space-y-2">
        <Skeleton height={14} width={`90%`} />
        <Skeleton height={14} width={`85%`} />
        <Skeleton height={14} width={`80%`} />
      </div>

      {/* Bouton ou action */}
      <div className="mt-6">
        <Skeleton height={36} width={`100%`} borderRadius={8} />
      </div>
    </div>
  );
};

export default SubscriptionForMeta;
